import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, Row, Col } from 'antd';
import PropTypes from 'prop-types';
import { CheckCircleOutlined, CheckSquareOutlined, CloseSquareOutlined, DownloadOutlined } from '@ant-design/icons';
import colors from '../constants/Colors';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import Permissions from '../auth/Permissions';
import { getWorkflowMarketplaceWorkflowImages } from './state/workflowMarketplace.actions';
import { componentIcons } from './workflowMarketplace.constants';

export default function WorkflowDetails({
  open,
  onClose,
  workflow,
  setImportWorkflow,
  relevantUser,
}) {
  const dispatch = useDispatch();
  const workflowImageMap = useSelector(
    (state) => state.workflowMarketplace.workflowImageMap,
  );

  const [selectedImage, setSelectedImage] = useState(null);
  const images = workflowImageMap[workflow?.name] || [];

  useEffect(() => {
    if (!open || !workflow?.name) return;
    setSelectedImage(null);
    dispatch(getWorkflowMarketplaceWorkflowImages(workflow.name));
  }, [open, workflow?.name]);

  if (!selectedImage && images.length) {
    setSelectedImage(images[0]);
  }

  const getRequirementIcon = (requirement) => {
    const truthyIcon = <CheckSquareOutlined style={{ color: colors.ONTRACCR_SUCCESS }} />;
    const falsyIcon = <CloseSquareOutlined style={{ color: colors.ONTRACCR_ERROR }} />;
    if (requirement.value === true) {
      return truthyIcon;
    }

    if (requirement.value === 'isAdmin') {
      return relevantUser?.position === 'Admin' ? truthyIcon : falsyIcon;
    }
    return falsyIcon;
  };

  if (!open) return null;

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      footer={null}
      width={1400}
      centered
      wrapClassName="workflow-details-modal-wrap"
      className="workflow-details-modal"
    >
      <Row className="workflow-details-header">
        <div className="workflow-details-title">IMPORT WORKFLOW</div>
      </Row>
      <Row>
        <Col span={16} className="workflow-details-main-container">
          <div className="workflow-details-description">
            {workflow.longDescription}
          </div>
          {selectedImage && (
            <img
              className="workflow-details-main-image"
              src={selectedImage.url}
              alt={selectedImage.key}
            />
          )}
          <Row className="workflow-details-reel-container">
            {images.map((image) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <img
                className={`workflow-details-reel-image ${selectedImage?.key === image.key ? 'selected' : ''}`}
                src={image.url}
                alt={image.key}
                onClick={() => setSelectedImage(image)}
                key={image.key}
              />
            ))}
          </Row>
          <Row style={{ marginTop: 16 }}>
            <div className="workflow-details-header-4">Components Included:</div>
            <Row className="workflow-details-components-container">
              {workflow.components.map((component) => (
                <div key={component.name} style={{ marginRight: 16 }}>
                  <Row style={{ alignItems: 'center', marginTop: 8 }}>
                    <div className="workflow-details-component-icon" style={{ backgroundColor: componentIcons[component.type].color }}>
                      {componentIcons[component.type].icon}
                    </div>
                    <span className="workflow-details-component-name">{component.name}</span>
                  </Row>
                  <ul className="workflow-details-component-list">
                    {component.values.map((value) => (
                      <li key={value} className="workflow-details-component-list-item">{value}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </Row>
          </Row>
          <Row>
            <div className="workflow-details-header-4">Automations Included:</div>
            {workflow.automations.map((automation) => (
              <div key={automation} className="workflow-details-automation-item">
                {automation}
              </div>
            ))}
          </Row>
        </Col>
        <Col span={8} className="workflow-details-sidebar-container">
          <Row>
            <div className="workflow-card-icon" style={{ backgroundColor: workflow.color }}>
              <workflow.icon />
            </div>
            <div style={{ width: 'calc(100% - 56px)' }}>
              <div className="workflow-card-name workflow-details-name">
                {workflow.name}
              </div>
            </div>
          </Row>
          <Row>
            <div className="workflow-details-short-description">
              {workflow.shortDescription}
            </div>
          </Row>
          {!!workflow.imported && (
            <Row className="workflow-details-imported-container">
              <div>
                <CheckCircleOutlined className="workflow-details-imported-icon" />
              </div>
              <div className="workflow-details-imported-text">Already Imported</div>
            </Row>
          )}
          {Permissions.has('IMPORT_WORKFLOW_MARKETPLACE') && (
            <OnTraccrButton
              title={workflow.imported ? 'Re-import template' : 'Import this template'}
              onClick={() => setImportWorkflow(workflow)}
              type={workflow.imported ? 'cancel' : 'primary'}
              style={{ width: '100%', height: 40, marginTop: 12 }}
              icon={<DownloadOutlined />}
            />
          )}
          <div className="workflow-divider" />
          <div className="workflow-details-sub-title">Category</div>
          <div className="workflow-card-categories" style={{ marginBottom: 20 }}>
            {workflow.categories.map((category) => (
              <div key={category} className="workflow-details-category-item">
                {category}
              </div>
            ))}
          </div>
          <div className="workflow-details-sub-title">Requirements</div>
          {workflow.requirements.map((requirement) => (
            <Row key={requirement.title}>
              <div>{getRequirementIcon(requirement)}</div>
              <div className="workflow-details-requirement-title">{requirement.title}</div>
            </Row>
          ))}
          <div className="workflow-details-sub-title" style={{ marginTop: 12 }}>Common Use Cases</div>
          {workflow.commonUseCases.map((useCase) => (
            <div key={useCase} className="workflow-details-common-use-case-item">
              {useCase}
            </div>
          ))}
        </Col>
      </Row>
    </Modal>
  );
}

WorkflowDetails.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workflow: PropTypes.shape({
    name: PropTypes.string.isRequired,
    shortDescription: PropTypes.string.isRequired,
    longDescription: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    requirements: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    })).isRequired,
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    commonUseCases: PropTypes.arrayOf(PropTypes.string).isRequired,
    components: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string.isRequired,
      values: PropTypes.arrayOf(PropTypes.string).isRequired,
    })).isRequired,
    automations: PropTypes.arrayOf(PropTypes.string).isRequired,
    imported: PropTypes.bool.isRequired,
  }).isRequired,
  setImportWorkflow: PropTypes.func.isRequired,
  relevantUser: PropTypes.shape({
    position: PropTypes.string.isRequired,
  }).isRequired,
};
